// Seed data for mock mode. Lets the whole app run and demo in a plain browser
// with no SharePoint/Graph. A three-person team:
//   Juan Dela Cruz — Management (manager): files his own leave AND approves his
//                    team's. His own approver, Erwin Reyes, is outside the demo.
//   Maria Santos   — Technical (employee), reports to Juan
//   Paolo Ramos    — Functional (employee), reports to Juan; on probation, so no VL
// Use "Viewing as" to switch: only Admin/Management get the Manager Queue.
//
// Request dates are relative to today so the demo never goes stale: there's
// always an upcoming leave, an urgent pending one, and a non-consecutive one.

import {
  CurrentUser,
  Employee,
  Holiday,
  LeaveBalance,
  LeaveRequest,
  LeaveType,
} from "../models/leaveModels";
import { todayISO } from "../utils/dateUtils";
import { addWorkingDays } from "../utils/workingDays";

export const MOCK_YEAR = new Date().getFullYear();

export const MOCK_CURRENT_USER: CurrentUser = {
  name: "Juan Dela Cruz",
  email: "juan.delacruz@kationtechnologies.com",
};

export const seedEmployees: Employee[] = [
  {
    id: "e1",
    name: "Juan Dela Cruz",
    email: "juan.delacruz@kationtechnologies.com",
    department: "Management",
    managerName: "Erwin Reyes",
    managerEmail: "erwin.reyes@kationtechnologies.com",
    hireDate: "2021-03-01",
    probationStatus: false,
    vlEntitlement: 10,
    slEntitlement: 15,
  },
  {
    id: "e2",
    name: "Maria Santos",
    email: "maria.santos@kationtechnologies.com",
    department: "Technical",
    managerName: "Juan Dela Cruz",
    managerEmail: "juan.delacruz@kationtechnologies.com",
    hireDate: "2022-07-15",
    probationStatus: false,
    vlEntitlement: 10,
    slEntitlement: 15,
  },
  {
    id: "e3",
    name: "Paolo Ramos",
    email: "paolo.ramos@kationtechnologies.com",
    department: "Functional",
    managerName: "Juan Dela Cruz",
    managerEmail: "juan.delacruz@kationtechnologies.com",
    hireDate: "2026-05-01",
    probationStatus: true, // still on probation — VL blocked
    vlEntitlement: 10,
    slEntitlement: 15,
  },
];

export const seedLeaveTypes: LeaveType[] = [
  {
    id: "lt1",
    lookupId: 1,
    title: "Vacation Leave",
    code: "VL",
    requiresMedCert: false,
    medCertThresholdDays: 0,
    requiresDocuments: false,
    deductsBalance: true,
    availableDuringProbation: false,
    leadTimeDays: 3,
  },
  {
    id: "lt2",
    lookupId: 2,
    title: "Sick Leave",
    code: "SL",
    requiresMedCert: true,
    medCertThresholdDays: 2,
    requiresDocuments: false,
    deductsBalance: true,
    availableDuringProbation: true,
    leadTimeDays: 0,
  },
  {
    id: "lt3",
    lookupId: 3,
    title: "Bereavement Leave",
    code: "BL",
    requiresMedCert: false,
    medCertThresholdDays: 0,
    requiresDocuments: true,
    deductsBalance: false,
    availableDuringProbation: true,
    leadTimeDays: 0,
  },
  {
    id: "lt4",
    lookupId: 4,
    title: "Emergency Leave",
    code: "EL",
    requiresMedCert: false,
    medCertThresholdDays: 0,
    requiresDocuments: false,
    deductsBalance: false,
    availableDuringProbation: true,
    leadTimeDays: 0,
  },
  {
    id: "lt5",
    lookupId: 5,
    title: "Maternity Leave",
    code: "ML",
    requiresMedCert: false,
    medCertThresholdDays: 0,
    requiresDocuments: true,
    deductsBalance: false,
    availableDuringProbation: true,
    leadTimeDays: 30,
  },
  {
    id: "lt6",
    lookupId: 6,
    title: "Paternity Leave",
    code: "PL",
    requiresMedCert: false,
    medCertThresholdDays: 0,
    requiresDocuments: true,
    deductsBalance: false,
    availableDuringProbation: true,
    leadTimeDays: 7,
  },
  {
    id: "lt7",
    lookupId: 7,
    title: "Solo Parent Leave",
    code: "SPL",
    requiresMedCert: false,
    medCertThresholdDays: 0,
    requiresDocuments: true,
    deductsBalance: false,
    availableDuringProbation: true,
    leadTimeDays: 5,
  },
  {
    id: "lt8",
    lookupId: 8,
    title: "Magna Carta Leave",
    code: "MC",
    requiresMedCert: true,
    medCertThresholdDays: 1,
    requiresDocuments: true,
    deductsBalance: false,
    availableDuringProbation: true,
    leadTimeDays: 5,
  },
  {
    id: "lt9",
    lookupId: 9,
    title: "Leave Without Pay",
    code: "LWOP",
    requiresMedCert: false,
    medCertThresholdDays: 0,
    requiresDocuments: false,
    deductsBalance: false,
    availableDuringProbation: true,
    leadTimeDays: 3,
  },
];

function balancesFor(email: string, vlUsed: number, slUsed: number): LeaveBalance[] {
  const emp = seedEmployees.find((e) => e.email === email)!;
  return [
    {
      id: `${email}-VL`,
      employeeEmail: email,
      year: MOCK_YEAR,
      leaveTypeCode: "VL",
      entitlement: emp.vlEntitlement,
      used: vlUsed,
      remaining: emp.vlEntitlement - vlUsed,
    },
    {
      id: `${email}-SL`,
      employeeEmail: email,
      year: MOCK_YEAR,
      leaveTypeCode: "SL",
      entitlement: emp.slEntitlement,
      used: slUsed,
      remaining: emp.slEntitlement - slUsed,
    },
  ];
}

export const seedBalances: LeaveBalance[] = [
  ...balancesFor("juan.delacruz@kationtechnologies.com", 3, 2),
  ...balancesFor("maria.santos@kationtechnologies.com", 5, 1),
  ...balancesFor("paolo.ramos@kationtechnologies.com", 0, 1),
];

// 2026 Philippine holidays (demo set — replace with the official Proclamation).
export const seedHolidays: Holiday[] = [
  { id: "h1", name: "New Year's Day", date: "2026-01-01", type: "Regular", year: 2026 },
  { id: "h2", name: "Chinese New Year", date: "2026-02-17", type: "Special Non-Working", year: 2026 },
  { id: "h3", name: "EDSA People Power Anniversary", date: "2026-02-25", type: "Special Non-Working", year: 2026 },
  { id: "h4", name: "Maundy Thursday", date: "2026-04-02", type: "Regular", year: 2026 },
  { id: "h5", name: "Good Friday", date: "2026-04-03", type: "Regular", year: 2026 },
  { id: "h6", name: "Black Saturday", date: "2026-04-04", type: "Special Non-Working", year: 2026 },
  { id: "h7", name: "Araw ng Kagitingan", date: "2026-04-09", type: "Regular", year: 2026 },
  { id: "h8", name: "Labor Day", date: "2026-05-01", type: "Regular", year: 2026 },
  { id: "h9", name: "Independence Day", date: "2026-06-12", type: "Regular", year: 2026 },
  { id: "h10", name: "Ninoy Aquino Day", date: "2026-08-21", type: "Special Non-Working", year: 2026 },
  { id: "h11", name: "National Heroes Day", date: "2026-08-31", type: "Regular", year: 2026 },
  { id: "h12", name: "All Saints' Day", date: "2026-11-01", type: "Special Non-Working", year: 2026 },
  { id: "h13", name: "Bonifacio Day", date: "2026-11-30", type: "Regular", year: 2026 },
  { id: "h14", name: "Immaculate Conception", date: "2026-12-08", type: "Special Non-Working", year: 2026 },
  { id: "h15", name: "Christmas Day", date: "2026-12-25", type: "Regular", year: 2026 },
  { id: "h16", name: "Rizal Day", date: "2026-12-30", type: "Regular", year: 2026 },
  { id: "h17", name: "New Year's Eve", date: "2026-12-31", type: "Special Non-Working", year: 2026 },
];

// --- Leave requests (relative to today) -------------------------------------

const TODAY = todayISO();
/** The nth working day from today (negative = past), skipping seed holidays. */
const wd = (n: number) => addWorkingDays(TODAY, n, seedHolidays);
/** An ISO datetime `n` calendar days ago. */
const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString();

function seedRequest(
  r: Omit<LeaveRequest, "startDate" | "endDate" | "workingDays" | "lwopDays"> &
    Partial<Pick<LeaveRequest, "lwopDays">>,
): LeaveRequest {
  const leaveDates = [...r.leaveDates].sort();
  return {
    ...r,
    leaveDates,
    startDate: leaveDates[0],
    endDate: leaveDates[leaveDates.length - 1],
    workingDays: leaveDates.length,
    lwopDays: r.lwopDays ?? 0,
  };
}

export const seedRequests: LeaveRequest[] = [
  seedRequest({
    id: "1001",
    title: "LR-1001",
    employee: "Juan Dela Cruz",
    employeeEmail: "juan.delacruz@kationtechnologies.com",
    leaveTypeId: 1,
    leaveTypeCode: "VL",
    leaveDates: [wd(-12), wd(-11), wd(-10)],
    reason: "Family trip to Baguio.",
    status: "Approved",
    filedDate: daysAgo(30),
    pendingSince: daysAgo(30),
    managerEmail: "erwin.reyes@kationtechnologies.com",
    approver: "erwin.reyes@kationtechnologies.com",
    decisionDate: daysAgo(29),
    decisionNotes: "Approved. Enjoy!",
  }),
  seedRequest({
    id: "1002",
    title: "LR-1002",
    employee: "Juan Dela Cruz",
    employeeEmail: "juan.delacruz@kationtechnologies.com",
    leaveTypeId: 2,
    leaveTypeCode: "SL",
    leaveDates: [wd(6), wd(7)],
    reason: "Scheduled minor procedure and recovery.",
    status: "Pending",
    filedDate: daysAgo(1),
    pendingSince: daysAgo(1),
    managerEmail: "erwin.reyes@kationtechnologies.com",
    attachmentUrl: "https://example.com/medcert.pdf",
    attachmentName: "medcert.pdf",
  }),
  // Non-consecutive, and close enough to be flagged urgent in the queue.
  seedRequest({
    id: "1003",
    title: "LR-1003",
    employee: "Maria Santos",
    employeeEmail: "maria.santos@kationtechnologies.com",
    leaveTypeId: 1,
    leaveTypeCode: "VL",
    leaveDates: [wd(3), wd(5)],
    reason: "Out-of-town wedding (ceremony and reception days).",
    status: "Pending",
    filedDate: daysAgo(6),
    pendingSince: daysAgo(6),
    managerEmail: "juan.delacruz@kationtechnologies.com",
  }),
  // Emergency leave filed the day after, as the policy allows.
  seedRequest({
    id: "1004",
    title: "LR-1004",
    employee: "Paolo Ramos",
    employeeEmail: "paolo.ramos@kationtechnologies.com",
    leaveTypeId: 4,
    leaveTypeCode: "EL",
    leaveDates: [wd(-1)],
    reason: "Household emergency: burst water pipe.",
    status: "Pending",
    filedDate: daysAgo(0),
    pendingSince: daysAgo(0),
    managerEmail: "juan.delacruz@kationtechnologies.com",
  }),
  seedRequest({
    id: "1005",
    title: "LR-1005",
    employee: "Maria Santos",
    employeeEmail: "maria.santos@kationtechnologies.com",
    leaveTypeId: 2,
    leaveTypeCode: "SL",
    leaveDates: [wd(-3)],
    reason: "Fever.",
    status: "Approved",
    filedDate: daysAgo(4),
    pendingSince: daysAgo(4),
    managerEmail: "juan.delacruz@kationtechnologies.com",
    approver: "juan.delacruz@kationtechnologies.com",
    decisionDate: daysAgo(4),
    decisionNotes: "Get well soon.",
  }),
  seedRequest({
    id: "1006",
    title: "LR-1006",
    employee: "Maria Santos",
    employeeEmail: "maria.santos@kationtechnologies.com",
    leaveTypeId: 1,
    leaveTypeCode: "VL",
    leaveDates: [wd(8), wd(9)],
    reason: "Moving to a new apartment.",
    status: "Approved",
    filedDate: daysAgo(12),
    pendingSince: daysAgo(12),
    managerEmail: "juan.delacruz@kationtechnologies.com",
    approver: "juan.delacruz@kationtechnologies.com",
    decisionDate: daysAgo(11),
    decisionNotes: "Approved.",
  }),
];
